import React, { useRef, useEffect, useState } from 'react';
import { X, Maximize2, ChevronUp, ChevronDown, Loader2, Trash2, Copy, Sparkles, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import Markdown from 'markdown-to-jsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useDocumentFontSize } from '@/hooks/useDocumentFontSize';
import { useResponseDetailLevel, RESPONSE_DETAIL_OPTIONS } from '@/hooks/useResponseDetailLevel';

interface QuestionHistoryItem {
  input_text: string;
  response: string | { output: string };
  timestamp: string;
  query_id?: string;
}

interface CustomQuestionSectionProps {
  showQuestionHistory: boolean;
  setShowQuestionHistory: (show: boolean) => void;
  questions: QuestionHistoryItem[];
  questionText: string;
  setQuestionText: (text: string) => void;
  questionLoading: boolean;
  handleQuestionSubmit: () => void;
  scrollDivRef: React.RefObject<HTMLDivElement>;
  showTopButton: boolean;
  showBottomButton: boolean;
  scrollToTop: () => void;
  scrollToBottom: () => void;
  toggleFullscreen?: () => void;
  isFullscreen?: boolean;
  onDeleteQuestion?: (queryId: string, index: number) => void;
  customPrompts?: Array<{ prompt_id: string; label: string; prompt: string }>;
  standardPrompts?: Array<{ prompt_id: string; label: string; prompt: string }>;
  onCreatePrompt?: () => void;
}

export const CustomQuestionSection: React.FC<CustomQuestionSectionProps> = ({
  showQuestionHistory,
  setShowQuestionHistory,
  questions,
  questionText,
  setQuestionText,
  questionLoading,
  handleQuestionSubmit,
  scrollDivRef,
  showTopButton,
  showBottomButton,
  scrollToTop,
  scrollToBottom,
  toggleFullscreen,
  isFullscreen,
  onDeleteQuestion,
  customPrompts = [],
  standardPrompts = [],
  onCreatePrompt,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [hoveredQuestionId, setHoveredQuestionId] = useState<number | null>(null);
  const [showQuickPromptsMenu, setShowQuickPromptsMenu] = useState(false);
  const { getFontSizeStyle } = useDocumentFontSize();
  const { responseDetailLevel, setResponseDetailLevel } = useResponseDetailLevel();

  // Auto-scroll to bottom when new messages arrive or when loading starts
  useEffect(() => {
    if (questions.length > 0) {
      scrollToBottom();
    }
  }, [questions.length]);

  // Also scroll when loading state changes (for the loading spinner)
  useEffect(() => {
    if (questionLoading) {
      scrollToBottom();
    }
  }, [questionLoading]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (questionText.trim() && !questionLoading) {
        handleQuestionSubmit();
      }
    }
  };

  // Handle copying question to clipboard
  const handleCopyQuestion = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy question:', error);
    }
  };

  // Handle quick prompt selection
  const handleQuickPromptSelect = (promptText: string) => {
    setQuestionText(promptText);
    setShowQuickPromptsMenu(false);
  };

  if (!showQuestionHistory) {
    return null;
  }

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex justify-between items-center p-3 border-b border-gray-200 bg-gray-50">
        <h3 className="font-semibold text-gray-900">Question History</h3>
        <div className="flex items-center gap-2">
          {/* Fullscreen button */}
          {toggleFullscreen && !isFullscreen && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              className="h-8 w-8 p-0"
              title="Fullscreen"
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
          )}
          {/* Close button */}
          {!isFullscreen && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowQuestionHistory(false)}
              className="h-8 w-8 p-0"
              title="Close"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Messages Container */}
      <div ref={scrollDivRef} className="relative flex-1 overflow-y-auto p-4 space-y-4">
        {questions && questions.length > 0 ? (
          <>
            {questions.map((item, idx) => {
              const isValidTimestamp = !isNaN(new Date(item.timestamp).getTime());

              // Handle response in different formats
              let responseText = '';
              if (typeof item.response === 'string') {
                responseText = item.response;
              } else if (typeof item.response === 'object' && item.response.output) {
                responseText = item.response.output;
              } else {
                responseText = JSON.stringify(item.response);
              }

              return (
                <div key={idx}>
                  {/* Divider */}
                  {idx > 0 && (
                    <div className="flex justify-center my-4">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                  )}

                  {/* Timestamp */}
                  {isValidTimestamp && (
                    <div className="text-center text-xs text-gray-500 mb-2">
                      {new Date(item.timestamp).toLocaleString()}
                    </div>
                  )}

                  {/* Question (Right-aligned, blue) */}
                  <div
                    className="flex justify-end mb-2 group"
                    onMouseEnter={() => setHoveredQuestionId(idx)}
                    onMouseLeave={() => setHoveredQuestionId(null)}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex items-start gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-gray-100"
                          onClick={() => handleCopyQuestion(item.input_text)}
                          title="Copy question"
                        >
                          <Copy className="w-4 h-4 text-gray-600" />
                        </Button>
                        {onDeleteQuestion && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-red-100"
                            onClick={() => onDeleteQuestion(item.query_id || '', idx)}
                            title="Delete question"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                      <div className="min-w-[250px] max-w-[75%] p-3 rounded-lg bg-blue-500 text-white shadow-sm">
                        <div className="whitespace-pre-wrap break-words" style={getFontSizeStyle()}>
                          {item.input_text.replace(/\n{2,}/g, '\n')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Response (Left-aligned, gray) */}
                  <div className="flex justify-start mb-2 group">
                    <div className="flex items-start gap-2">
                      <div className="min-w-[250px] max-w-[75%] p-3 rounded-lg bg-gray-100 border border-gray-200 text-gray-900 shadow-sm">
                        {responseText === '...' ? (
                          <div className="flex items-center gap-2 text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Generating response...</span>
                          </div>
                        ) : (
                          <div className="prose max-w-none text-gray-900" style={getFontSizeStyle()}>
                            <Markdown>{responseText}</Markdown>
                          </div>
                        )}
                      </div>
                      <div className="flex items-start gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-gray-100"
                          onClick={() => handleCopyQuestion(responseText)}
                          title="Copy response"
                        >
                          <Copy className="w-4 h-4 text-gray-600" />
                        </Button>
                        {onDeleteQuestion && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-red-100"
                            onClick={() => onDeleteQuestion(item.query_id || '', idx)}
                            title="Delete conversation"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            No question history yet. Ask a question about this document!
          </div>
        )}

        {/* Scroll buttons */}
        {showTopButton && (
          <Button
            variant="outline"
            size="sm"
            className="absolute top-4 right-4 rounded-full shadow-lg"
            onClick={scrollToTop}
            title="Scroll to top"
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
        )}
        {showBottomButton && (
          <Button
            variant="outline"
            size="sm"
            className="absolute bottom-4 right-4 rounded-full shadow-lg"
            onClick={scrollToBottom}
            title="Scroll to bottom"
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 p-4 bg-gray-50">
        <div className="flex gap-3">
          <Textarea
            className="flex-grow resize-none min-h-[60px] max-h-[120px]"
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question about this document..."
            disabled={questionLoading}
          />
          <div className="flex flex-col gap-2">
            {/* Quick Prompts Dropdown */}
            {((customPrompts && customPrompts.length > 0) || (standardPrompts && standardPrompts.length > 0)) && (
              <DropdownMenu open={showQuickPromptsMenu} onOpenChange={setShowQuickPromptsMenu}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-3 h-9"
                    disabled={questionLoading}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    <span className="text-xs">Quick Prompts</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 max-h-96 overflow-y-auto">
                  {/* Create New Prompt Button */}
                  {onCreatePrompt && (
                    <>
                      <DropdownMenuItem
                        onClick={onCreatePrompt}
                        className="cursor-pointer text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Create New Prompt
                      </DropdownMenuItem>
                      <div className="border-t border-gray-200 my-1" />
                    </>
                  )}

                  {/* Custom Prompts Section */}
                  {customPrompts && customPrompts.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Your Custom Prompts
                      </div>
                      {customPrompts.map((prompt) => (
                        <DropdownMenuItem
                          key={prompt.prompt_id}
                          onClick={() => handleQuickPromptSelect(prompt.prompt)}
                          className="cursor-pointer py-2"
                        >
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-sm">{prompt.label}</span>
                            <span className="text-xs text-gray-500 line-clamp-2">{prompt.prompt}</span>
                          </div>
                        </DropdownMenuItem>
                      ))}
                      <div className="border-t border-gray-200 my-1" />
                    </>
                  )}

                  {/* Standard Document Prompts Section */}
                  {standardPrompts && standardPrompts.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Standard Document Prompts
                      </div>
                      {standardPrompts.map((prompt) => (
                        <DropdownMenuItem
                          key={prompt.prompt_id}
                          onClick={() => handleQuickPromptSelect(prompt.prompt)}
                          className="cursor-pointer py-2"
                        >
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-sm">{prompt.label}</span>
                            <span className="text-xs text-gray-500 line-clamp-2">{prompt.prompt}</span>
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Response Detail Level Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="px-2 h-9 gap-1 text-xs"
                  title="Response detail level"
                  disabled={questionLoading}
                >
                  <FileText className="w-3.5 h-3.5" />
                  {RESPONSE_DETAIL_OPTIONS.find(opt => opt.value === responseDetailLevel)?.label || 'Medium'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {RESPONSE_DETAIL_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setResponseDetailLevel(option.value)}
                    className={`cursor-pointer ${responseDetailLevel === option.value ? 'bg-blue-50 text-blue-700' : ''}`}
                  >
                    <div className="flex flex-col flex-1">
                      <span className="text-sm font-medium">{option.label}</span>
                      <span className="text-xs text-gray-500">{option.description}</span>
                    </div>
                    {responseDetailLevel === option.value && (
                      <span className="ml-auto text-blue-600">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              onClick={handleQuestionSubmit}
              disabled={questionLoading || !questionText.trim()}
              className="px-6"
            >
              {questionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-2">
          Press Enter to submit, Shift+Enter for new line
        </div>
      </div>
    </div>
  );
};
